import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { tagService, CreateTagRequest } from '../../services/tagService';
import { useToast } from '../UI/ToastProvider';

interface CreateTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateTagModal: React.FC<CreateTagModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [tagName, setTagName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const handleInputChange = (value: string) => {
    // Converter maiúsculas para minúsculas
    let cleanValue = value.toLowerCase();
    
    // Substituir espaços por hífens
    cleanValue = cleanValue.replace(/\s+/g, '-');
    
    // Remover caracteres especiais, mantendo apenas letras, números e hífens
    cleanValue = cleanValue.replace(/[^a-z0-9-]/g, '');
    
    // Remover hífens duplicados
    cleanValue = cleanValue.replace(/-+/g, '-');
    
    // Remover hífens no início e fim
    cleanValue = cleanValue.replace(/^-+|-+$/g, '');
    
    setTagName(cleanValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tagName.trim()) {
      addToast('Nome da tag é obrigatório', 'error');
      return;
    }

    if (tagName.length < 2) {
      addToast('Nome da tag deve ter pelo menos 2 caracteres', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const tagData: CreateTagRequest = {
        name: tagName,
        type: 'document'
      };
      
      await tagService.createTag(tagData);
      addToast('Tag criada com sucesso!', 'success');
      onSuccess();
      onClose();
      setTagName('');
    } catch (error) {
      addToast('Erro ao criar tag. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Nova Tag</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="tagName" className="block text-sm font-medium text-gray-700 mb-1">
              Nome da Tag *
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                #
              </span>
              <div className="flex-1 relative">
                <input
                  type="text"
                  id="tagName"
                  value={tagName}
                  onChange={(e) => handleInputChange(e.target.value.slice(0, 50))}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    tagName.length >= 50 ? 'border-red-300 focus:ring-red-500' : ''
                  }`}
                  placeholder="nome-da-tag"
                  required
                  maxLength={50}
                />
                <div className="absolute -bottom-6 right-0 text-xs text-gray-500">
                  {tagName.length}/50
                </div>
              </div>
            </div>
            {tagName.length >= 50 && (
              <p className="text-xs text-red-500 mt-1">
                Limite máximo de 50 caracteres atingido
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Apenas letras, números e hífens.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Criando...' : 'Criar Tag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 